import { setDbManagerTabs, setDbTabLeaveGuard } from '../../store/tabsStore';
import { buildFilterFragment } from '../dbGridSql';
import { isOpaqueCell } from '../dbCellTags';
import { cellToText } from '../dbCellText';
import { parseCellKey } from '../dbGridModel';
import { setVisibleColumns } from '../dbColumnVisibility';
import { liveLevels } from '../dbGridSort';
import DbGridFindBar from '../DbGridFindBar';
import DbColumnPickerModal from '../DbColumnPickerModal';
import DbQueryHistoryModal from '../DbQueryHistoryModal';
import DbPasswordPromptModal from '../DbPasswordPromptModal';
import { resultToCsv, resultToJson, resultToInsertSql } from '../dbExportUtils';
import DbCellDetailModal from '../DbCellDetailModal';
import DbDatePicker from '../DbDatePicker';
import DbExportModal from '../DbExportModal';
import DbGridContextMenu from './DbGridContextMenu';
import { submenu, trimSeparators } from './dbGridMenuItems';
import { selectedRowIndexes } from './dbExportSelection';
import { ConfirmModal } from '../ui/Modal';
import { engineOf, normalizeSqlText, resolveColumnSuggestionState } from './dbResultsTabHelpers';

/** Context menu, overlays, filter input handlers and find bar for the results tab. Plain function: it runs during render and holds no hooks. */
export function dbResultsOverlays({
  tab,
  result,
  find,
  leaveGuard,
  isQueryTab,
  filterDraft,
  setFilterDraft,
  setSqlDraft,
  error,
  loading,
  pendingRows,
  pendingEdits,
  pendingDeletes,
  saving,
  primaryKeyCols,
  datePicker,
  setDatePicker,
  tableColumns,
  confirmRequest,
  setConfirmRequest,
  colSuggestions,
  setColSuggestions,
  columnTypeMap,
  enumByColumn,
  sortRef,
  totalRows,
  selectedRows,
  selectedCells,
  findOpen,
  findFocusNonce,
  findDraft,
  setFindDraft,
  findMatchCase,
  setFindMatchCase,
  findIndex,
  contextMenu,
  setContextMenu,
  cellViewer,
  setCellViewer,
  exportOpen,
  setExportOpen,
  columnPickerOpen,
  setColumnPickerOpen,
  exportRows,
  setExportRows,
  historyOpen,
  setHistoryOpen,
  passwordPrompt,
  filterInputRef,
  hasUnsavedGridChanges,
  commitFilter,
  exportFetchChunk,
  stageCellNull,
  stageCellValue,
  applyDatePick,
  clearDatePick,
  openCellViewer,
  fkByColumn,
  columnWidths,
  hiddenColumnNames,
  hiddenLive,
  setHiddenColumns,
  rowsForAction,
  resultForRows,
  hideColumn,
  handleColumnResize,
  pendingEditCount,
  refreshRows,
  discardChanges,
  stageDeleteRows,
  unstageDeleteRows,
  duplicateRow,
  importDump,
  stepFind,
  closeFind,
  applyFilterFragment,
  handleOrderColumn,
  handleClearSortColumn,
}) {
  // Right-click context menu for the grid. Cell/row actions when the click
  // landed on a cell (copy everywhere; edit/NULL/duplicate/delete only on
  // table tabs — query-tab grids are read-only), plus table-level actions
  // (Export/Import) on table tabs — those also work from a background
  // right-click (rowIndex null), so an empty table can still be exported to
  // or imported into.
  const renderContextMenu = () => {
    if (!contextMenu || !result) return null;
    const { rowIndex, colIndex } = contextMenu;
    const hasCell = rowIndex != null && !!result.rows[rowIndex];
    const engine = engineOf(tab);
    const copyText = (text) => navigator.clipboard.writeText(text).catch(() => {});
    const writable = !isQueryTab && !loading && !error;

    /** "Filter: col = value"-style entries for the clicked cell. */
    const buildFilterMenuItems = (effRow) => {
      const column = result.columns[colIndex];
      if (!column) return [];
      const value = effRow[colIndex];
      const blocked = hasUnsavedGridChanges || loading;
      const blockedTitle = blocked ? 'Save or discard pending changes first' : undefined;
      const label = (text) => {
        const str = cellToText(text);
        return str.length > 24 ? `${str.slice(0, 24)}…` : str;
      };
      const entry = (text, op, values) => ({
        label: text,
        disabled: blocked,
        title: blockedTitle,
        run: () => applyFilterFragment(buildFilterFragment(engine, column, op, values)),
      });

      const out = [];
      if (value === null || value === undefined) {
        out.push(entry(`${column} IS NULL`, 'null', []));
        out.push(entry(`${column} IS NOT NULL`, 'notnull', []));
        return out;
      }
      if (isOpaqueCell(value)) {
        // Its on-screen form is a summary, so `= <summary>` would match nothing.
        out.push(entry(`${column} IS NOT NULL`, 'notnull', []));
        return out;
      }

      // Several cells selected in this column: offer the set rather than the
      // one that happened to be right-clicked.
      const columnValues = [];
      for (const key of selectedCells) {
        const parsed = parseCellKey(key);
        if (!parsed || parsed.kind !== 'r' || parsed.col !== colIndex) continue;
        const editKey = `${parsed.id}:${colIndex}`;
        const v = Object.prototype.hasOwnProperty.call(pendingEdits, editKey)
          ? pendingEdits[editKey]
          : result.rows[parsed.id]?.[colIndex];
        columnValues.push(v);
      }

      out.push(entry(`${column} = ${label(value)}`, 'eq', [value]));
      out.push(entry(`${column} ≠ ${label(value)}`, 'ne', [value]));
      // A LIKE on a very long value is unwieldy and rarely what is wanted.
      if (cellToText(value).length <= 256) {
        out.push(entry(`${column} contains ${label(value)}`, 'like', [value]));
      }
      if (columnValues.length > 1) {
        out.push(entry(`${column} IN (${columnValues.length} values)`, 'in', columnValues));
      }
      return out;
    };

    const items = [];
    // Column header menu — a separate item set, not an addition to the cell
    // one: nothing here acts on a row.
    if (contextMenu.column) {
      const col = contextMenu.column;
      // Ordering is a table-browse action: it works by re-running the browse
      // query with an ORDER BY. A query tab's SQL is the user's own, and
      // appending ORDER BY to arbitrary SQL is not generally valid — it may
      // already have one, or be a CTE, a UNION, or not a SELECT at all.
      if (!isQueryTab && tab?.tableName) {
        const level = liveLevels(sortRef.current, result?.columns).find((l) => l.col === col);
        items.push(
          {
            label: `Order by “${col}” ASC`,
            checked: level?.dir === 'asc',
            run: () => handleOrderColumn(col, 'asc'),
          },
          {
            label: `Order by “${col}” DESC`,
            checked: level?.dir === 'desc',
            run: () => handleOrderColumn(col, 'desc'),
          },
          // Only when this column IS a sort level. Without it the menu can set
          // an order but never clear one — the header click cycles to off, but
          // somebody working from the menu should not have to discover that.
          ...(level ? [{ label: `Remove sort on “${col}”`, run: () => handleClearSortColumn(col) }] : []),
          { separator: true }
        );
      }
      items.push(
        { label: `Hide “${col}”`, run: () => hideColumn(col) },
        {
          label: hiddenLive.length > 0 ? `Show all columns (${hiddenLive.length} hidden)` : 'Show all columns',
          disabled: hiddenLive.length === 0,
          run: () => setHiddenColumns([]),
        },
        { label: 'Choose columns…', run: () => setColumnPickerOpen(true) },
        { separator: true },
        {
          label: 'Reset column width',
          disabled: !columnWidths[col],
          run: () => handleColumnResize(col, null),
        }
      );
    }
    if (hasCell) {
      const effRow = result.columns.map((_, j) => {
        const key = `${rowIndex}:${j}`;
        return Object.prototype.hasOwnProperty.call(pendingEdits, key)
          ? pendingEdits[key]
          : result.rows[rowIndex][j];
      });
      const targets = rowsForAction(rowIndex);
      const noun = `${targets.length} row${targets.length === 1 ? '' : 's'}`;
      items.push(
        submenu('Copy', [
          { label: 'Cell', run: () => copyText(cellToText(effRow[colIndex])) },
          {
            label: 'Row as JSON',
            run: () =>
              copyText(
                JSON.stringify(Object.fromEntries(result.columns.map((c, i) => [c, effRow[i]])), null, 2)
              ),
          },
          {
            label: 'Row as SQL INSERT',
            run: () =>
              copyText(resultToInsertSql(engine, tab.tableName || 'query_result', result.columns, [effRow])),
          },
          ...(targets.length === 0
            ? []
            : [
              { separator: true },
              {
                label: `${noun} as CSV`,
                run: () => copyText(resultToCsv(resultForRows(targets))),
              },
              {
                label: `${noun} as JSON`,
                run: () => copyText(resultToJson(resultForRows(targets))),
              },
              ...(tab?.tableName
                ? [{
                    label: `${noun} as SQL INSERT`,
                    run: () =>
                      copyText(
                        resultToInsertSql(
                          engine,
                          tab.tableName,
                          result.columns,
                          resultForRows(targets).rows
                        )
                      ),
                  }]
                : []),
            ]),
        ]),
        ...(targets.length > 0
          ? [{ label: `Export ${noun}…`, run: () => { setExportRows(targets); setExportOpen(true); } }]
          : []),
        ...(!isQueryTab && tab?.tableName ? [{ separator: true }, submenu('Filter', buildFilterMenuItems(effRow))] : []),
        ...(writable
          ? [
              { separator: true },
              { label: 'Edit cell…', run: () => openCellViewer(rowIndex, colIndex, false) },
              { label: 'Set cell NULL', run: () => stageCellNull(rowIndex, colIndex) },
              { label: 'Duplicate row', run: () => duplicateRow(rowIndex) },
              { separator: true },
              // No ellipsis any more: this no longer opens a dialog, it marks
              // the row and Save commits it. The label says which way the
              // toggle will go for the row under the pointer.
              pendingDeletes.has(rowIndex)
                ? { label: 'Keep row', run: () => unstageDeleteRows(new Set([rowIndex])) }
                : { label: 'Mark row for deletion', danger: true, run: () => stageDeleteRows(new Set([rowIndex])) },
            ]
          : [{ label: 'View cell…', run: () => openCellViewer(rowIndex, colIndex, true) }])
      );
    }
    // Export/Import are table-level, so they belong on the cell and background
    // menus but not on the header one, where nothing else acts on the table.
    if (!isQueryTab && tab?.tableName && !contextMenu.column) {
      if (items.length > 0) items.push({ separator: true });
      items.push(
        {
          label: 'Refresh',
          run: refreshRows,
          disabled: hasUnsavedGridChanges || loading,
          title: hasUnsavedGridChanges ? 'Save or discard pending changes first' : undefined,
        },
        { label: 'Export…', run: () => { setExportRows(selectedRowIndexes(selectedRows, selectedCells)); setExportOpen(true); } },
        {
          label: 'Import SQL dump…',
          run: importDump,
          disabled: hasUnsavedGridChanges || loading || saving,
          title: 'Save or discard pending changes first',
        }
      );
    }
    const shown = trimSeparators(items); // a group with nothing in it leaves no stray separator
    if (shown.length === 0) return null;
    return (
      <DbGridContextMenu
        key={`${contextMenu.x}:${contextMenu.y}`}
        items={shown}
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={() => setContextMenu(null)}
      />
    );
  };

  // Shared modal overlays — rendered by both the query-tab and table-tab
  // return branches.
  const overlays = (
    <>
      {renderContextMenu()}
      {datePicker && (
        <DbDatePicker
          kind={datePicker.kind}
          value={datePicker.value}
          anchorRect={datePicker.rect}
          onPick={applyDatePick}
          onClear={clearDatePick}
          onClose={() => setDatePicker(null)}
        />
      )}
      {cellViewer && (
        <DbCellDetailModal
          open
          columnName={result?.columns?.[cellViewer.colIndex] ?? ''}
          columnType={columnTypeMap[result?.columns?.[cellViewer.colIndex]]}
          enumMeta={isQueryTab ? null : enumByColumn.get(result?.columns?.[cellViewer.colIndex]) || null}
          value={cellViewer.value}
          readOnly={cellViewer.readOnly}
          onClose={() => setCellViewer(null)}
          onSave={(v) => stageCellValue(cellViewer.rowIndex, cellViewer.colIndex, v)}
          // Query tabs have no constraint metadata — their columns are an
          // arbitrary projection — so the dictionary is table-tab only.
          fk={isQueryTab ? null : fkByColumn?.get(result?.columns?.[cellViewer.colIndex])?.fk}
          fkContext={
            isQueryTab || !tab
              ? null
              : {
                  engine: engineOf(tab),
                  extensionId: tab.extensionId,
                  connId: tab.connId,
                  dbName: tab.dbName,
                }
          }
        />
      )}
      {passwordPrompt && (
        <DbPasswordPromptModal
          open
          connectionLabel={passwordPrompt.label}
          user={passwordPrompt.user}
          onSubmit={passwordPrompt.submit}
          onClose={passwordPrompt.cancel}
        />
      )}
      {historyOpen && (
        <DbQueryHistoryModal
          open
          connId={tab?.connId || null}
          onPick={(sql) => {
            // Replace the draft rather than appending: History is "run that
            // again", and the editor commits per keystroke anyway.
            const next = normalizeSqlText(sql);
            setSqlDraft(next);
            setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, sql: next } : t)));
          }}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {columnPickerOpen && result && (
        <DbColumnPickerModal
          open
          columns={result.columns}
          hidden={hiddenColumnNames}
          onApply={(visible) => {
            const next = setVisibleColumns(result.columns, visible);
            if (next) setHiddenColumns(next);
          }}
          onClose={() => setColumnPickerOpen(false)}
        />
      )}
      {exportOpen && (
        <DbExportModal
          open
          onClose={() => {
            setExportOpen(false);
            setExportRows(null);
          }}
          engine={engineOf(tab)}
          tableName={tab.tableName || null}
          // The selection is its own scope ("Selected rows", default when
          // present); Current result and Entire table stay available.
          selection={exportRows?.length ? resultForRows(exportRows) : null}
          result={result}
          totalRows={isQueryTab ? null : totalRows}
          fetchChunk={isQueryTab ? null : exportFetchChunk}
          // Chunked paging needs a total order; without a PK (or sqlite's
          // rowid) the modal falls back to one un-paged snapshot query.
          chunkable={primaryKeyCols.length > 0 || engineOf(tab) === 'sqlite'}
        />
      )}
      {confirmRequest && (
        <ConfirmModal
          isOpen
          onClose={() => setConfirmRequest(null)}
          onConfirm={confirmRequest.onConfirm}
          title={confirmRequest.title}
          message={confirmRequest.message}
          confirmText={confirmRequest.confirmText}
          type={confirmRequest.type || 'default'}
          hideCancel={!!confirmRequest.hideCancel}
        />
      )}
      {leaveGuard && (
        <ConfirmModal
          isOpen
          onClose={() => setDbTabLeaveGuard(null)}
          onConfirm={() => {
            const { proceed } = leaveGuard;
            // Discard first: it empties this tab's staged state AND clears the
            // hasUnsavedChanges flag, so the re-run below sails past the guard
            // instead of re-arming it.
            discardChanges();
            setDbManagerTabs((prev) =>
              prev.map((t) => (t.id === tab.id ? { ...t, hasUnsavedChanges: false } : t))
            );
            setDbTabLeaveGuard(null);
            proceed();
          }}
          title="Unsaved changes"
          message={`This table has ${pendingEditCount} staged edit${pendingEditCount === 1 ? '' : 's'}${
            pendingRows.length
              ? ` and ${pendingRows.length} new row${pendingRows.length === 1 ? '' : 's'}`
              : ''
          } that will be discarded if you leave.`}
          confirmText="Discard & leave"
          type="danger"
        />
      )}
    </>
  );

  const handleFilterChange = (e) => {
    const value = normalizeSqlText(e.target.value);
    setFilterDraft(value);
    const cursorPos = e.target.selectionStart;
    const state = tableColumns.length
      ? resolveColumnSuggestionState(value.slice(0, cursorPos), cursorPos, tableColumns)
      : null;
    if (state && filterInputRef.current) {
      const rect = filterInputRef.current.getBoundingClientRect();
      setColSuggestions({ ...state, x: rect.left, y: rect.bottom + 4, active: 0 });
    } else {
      setColSuggestions(null);
    }
  };

  const insertColumnSuggestion = (col) => {
    const { insertStart, insertEnd } = colSuggestions;
    const before = filterDraft.slice(0, insertStart);
    const after = filterDraft.slice(insertEnd);
    setFilterDraft(`${before}${col.name}${after}`);
    setColSuggestions(null);
    requestAnimationFrame(() => {
      if (filterInputRef.current) {
        const pos = before.length + col.name.length;
        filterInputRef.current.focus();
        filterInputRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleFilterKeyDown = (e) => {
    if (colSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setColSuggestions((prev) => (prev ? { ...prev, active: Math.min(prev.active + 1, prev.items.length - 1) } : null));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setColSuggestions((prev) => (prev ? { ...prev, active: Math.max(prev.active - 1, 0) } : null));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertColumnSuggestion(colSuggestions.items[colSuggestions.active]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setColSuggestions(null);
        return;
      }
    }
    if (e.key === 'Enter') commitFilter();
  };

  // One bar, rendered above whichever grid the tab is showing.
  const findBarNode = findOpen ? (
    <DbGridFindBar
      query={findDraft}
      onQueryChange={setFindDraft}
      matchCase={findMatchCase}
      onMatchCaseChange={setFindMatchCase}
      matchCount={find.matches.length}
      activeIndex={findIndex}
      truncated={find.truncated}
      onNext={() => stepFind(1)}
      onPrev={() => stepFind(-1)}
      onClose={closeFind}
      focusNonce={findFocusNonce}
    />
  ) : null;

  return { overlays, handleFilterChange, insertColumnSuggestion, handleFilterKeyDown, findBarNode };
}
